import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import { getUserInfo } from './apiUtils.js';
import { ChevronDown, Search, SortAsc, SortDesc, Calendar, Filter, X, Plus, AlertTriangle } from 'lucide-react';




// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';







const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '400px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  title: {
    fontSize: '18px',
    textAlign: 'center',
    margin: 0,
    color: '#000',
    backgroundColor: 'white',
  },
  button: {
    backgroundColor: '#FFFDD0',
    border: 'none',
    borderRadius: '4px',
    padding: '10px',
    width: '100%',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    color: 'black'
  }
};

const Dialog = ({ message, onClose, onCancel }) => {
  const isConfirmation = message.startsWith('Are you sure');
  const isEmptyDomains = message.startsWith('No Functional Domains found');

  const formattedMessage = message.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < message.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));

  return (
    <div style={DialogStyles.overlay}>
      <div style={{
        ...DialogStyles.dialog,
        borderRadius: isEmptyDomains ? '16px' : '8px',
        border: isEmptyDomains ? '1px solid #ccc' : 'none',
      }}>
        <h2 style={{
          ...DialogStyles.title,
          fontSize: isEmptyDomains ? '16.8px' : '18px',
          lineHeight: isEmptyDomains ? '1.4' : '1.2',
        }}>
          {formattedMessage}
        </h2>
        {isConfirmation ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              style={{
                ...DialogStyles.button,
                backgroundColor: '#FF8F89',
                color: 'white',
                width: '50%'
              }}
              onClick={onClose}
            >
              Yes
            </button>
            <button
              style={{
                ...DialogStyles.button,
                width: '50%'
              }}
              onClick={onCancel}
            >
              No
            </button>
          </div>
        ) : (
          <button
            style={{
              ...DialogStyles.button,
              backgroundColor: isEmptyDomains ? '#FFFDD0' : '#FFFDD0',
              width: isEmptyDomains ? '120px' : '100%',
              margin: isEmptyDomains ? '0 auto' : '0',
              display: 'block'
            }}
            onClick={onClose}
          >
            OK
          </button>
        )}
      </div>
    </div>
  );
};

const CreateFunctionalDomainDialog = ({ isOpen, onClose, onSubmit, corporateAccount, isEditMode, domainToEdit }) => {
  const [domainName, setDomainName] = useState('');
  const [error, setError] = useState('');
  const domainNameInputRef = useRef(null);
  const login_token = localStorage.getItem('authToken');

  useEffect(() => {
    if (isEditMode && domainToEdit) {
      setDomainName(domainToEdit.functional_domain);
    } else {
      setDomainName('');
    }
  }, [isEditMode, domainToEdit]);

  useEffect(() => {
    if (isOpen && !isEditMode) {
      setDomainName('');
      setError('');
    } else if (isEditMode) {
      setError('');
    }
  }, [isOpen, isEditMode]);

  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen && domainNameInputRef.current) {
      domainNameInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const endpoint = isEditMode ?
      appConfig.getUrl('/update_functional_domain') :
      appConfig.getUrl('/create_functional_domain');

    const requestBody = isEditMode ? {
      corporate_account: corporateAccount,
      functional_domain_prev: domainToEdit.functional_domain,
      functional_domain: domainName
    } : {
      corporate_account: corporateAccount,
      functional_domain: domainName
    };

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'Success') {
        setError(data.status_description || 'Functional domain saved successfully');

        // Delay the refresh to allow user to see the success message
        setTimeout(() => {
          onSubmit(); // Refresh the domains list

          if (isEditMode) {
            // Close dialog after successful edit
            setTimeout(() => onClose(), 10);
          } else {
            // Clear form for new entry in create mode
            setDomainName('');
            domainNameInputRef.current?.focus();
          }
        }, 3000);
      } else {
        throw new Error(data.status_description || 'Failed to save functional domain');
      }
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      setError(err.message || 'An unexpected error occurred');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={DialogStyles.overlay}>
      <div style={{...DialogStyles.dialog, width: '500px'}}>
        <h2 style={{ ...DialogStyles.title, backgroundColor: '#CCCCFF' }}>
          {isEditMode ? 'Edit Functional Domain' : 'Create New Functional Domain'}
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={{marginBottom: '15px'}}>
            <label style={{display: 'block', marginBottom: '5px'}}>Domain Name*:</label>
            <input
              type="text"
              value={domainName}
              ref={domainNameInputRef}
              onChange={(e) => setDomainName(e.target.value)}
              onKeyDown={handleKeyDown}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              required
            />
          </div>
          {error && (
            <div className={`mb-4 p-2 rounded ${
              error.includes('successfully')
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-500'
            }`}>
              {error}
            </div>
          )}
          <div className="flex justify-center gap-4">
            <button
              type="button"
              onClick={onClose}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                backgroundColor: '#C8F4DE',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {isEditMode ? 'Update' : 'Add New Domain'}
            </button>
          </div>
          {!isEditMode && (
            <p className="text-gray-600 text-sm mt-4 text-center">
              You can continue adding more domains. Click 'Close' when finished.
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

const FunctionalDomains = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [redirectPath, setRedirectPath] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDomainForEdit, setSelectedDomainForEdit] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredDomains, setFilteredDomains] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [permissions, setPermissions] = useState({
    canUpdate: false,
    canDelete: false
  });

  const login_token = localStorage.getItem('authToken');
  const [domains, setDomains] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [corporateAccount, setCorporateAccount] = useState('');
  const [userId, setUserId] = useState('');
  const [accountDescription, setAccountDescription] = useState('');
  const [userName, setUserName] = useState('');

  // State for pagination
  const [itemsPerPage, setItemsPerPage] = useState(10);
const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
const [itemToDelete, setItemToDelete] = useState(null);

  // Sorting functionality
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Apply sorting to domains
  const sortedDomains = React.useMemo(() => {
    let sortableDomains = [...filteredDomains];
    if (sortConfig.key) {
      sortableDomains.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableDomains;
  }, [filteredDomains, sortConfig]);

  const styles = {
    deleteButton: {
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      padding: '6px 12px',
      height: '28px',
      fontSize: '0.8rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    editButton: {
      backgroundColor: '#4CAF50',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      padding: '6px 12px',
      height: '28px',
      fontSize: '0.8rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    disabled: {
      opacity: 0.6
    },
    container: {
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '0px',
      backgroundColor: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      fontSize: '0.875rem',
    },
    th: {
      backgroundColor: '#E5E4E2',
      color: '#0064e5',
      padding: '6px 12px',
      textAlign: 'left',
      borderBottom: '2px solid #ddd',
      fontSize: '0.875rem',
      lineHeight: '1',
      height: '32px',
      cursor: 'pointer',
      userSelect: 'none',
      position: 'relative'
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #ddd',
    },
    checkbox: {
      marginRight: '10px',
    },
    button: {
      padding: '6px 12px',
      margin: '0 5px',
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.8rem',
      height: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    error: {
      color: 'red',
      padding: '10px',
      marginBottom: '10px',
    },
  };


const renderDeleteConfirmDialog = () => {
  if (!isDeleteConfirmOpen || !itemToDelete) return null;

  return (
    <div style={{...DialogStyles.overlay, zIndex: 1100}}>
      <div style={{...DialogStyles.dialog, width: '600px'}}>
        <div style={{
          backgroundColor: '#FFE6E6',
          color: '#CC0000',
          padding: '15px 0',
          textAlign: 'center',
          fontSize: '18px',
          fontWeight: 'bold',
          borderRadius: '8px 8px 0 0',
          margin: '-20px -20px 20px -20px'
        }}>
          <div className="flex items-center justify-center gap-2">
            <AlertTriangle size={20} />
            <span>Confirm Deletion</span>
          </div>
        </div>

        <div className="px-6 py-4">
          <p className="text-gray-700 mb-4">
            {itemToDelete.id === 'multiple'
              ? `Are you sure you want to delete these functional domains?`
              : `Are you sure you want to delete this functional domain?`}
          </p>
          <p className="text-sm text-red-600 mb-4">
            This action cannot be undone.
          </p>

          {/* Show domain details */}
          <div className="bg-gray-50 rounded p-3 mb-4">
            <p className="text-xs text-gray-600 mb-2">Domain details:</p>
            <div className="text-sm text-gray-700">
              {itemToDelete.id === 'multiple' ? (
                <div><strong>Selected Domains:</strong> {itemToDelete.description}</div>
              ) : (
                <>
                  <div><strong>Domain Name:</strong> {itemToDelete.functional_domain}</div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-4 p-6 pt-0">
          <button
            onClick={() => {
              setIsDeleteConfirmOpen(false);
              setItemToDelete(null);
            }}
            style={{
              backgroundColor: '#f8f9fa',
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteConfirmed}
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {itemToDelete.id === 'multiple'
              ? 'Delete Functional Domains'
              : 'Delete Functional Domain'}
          </button>
        </div>
      </div>
    </div>
  );
};

  const checkPermission = async (categoryHeader) => {
    try {

    console.log("check permissions url", appConfig.getUrl('/get_user_access_level_new'));
      const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          user_id: userId,
          category_header: categoryHeader,
          category_sub_header: []
        }),
      });
      const data = await response.json();
      // Create new permissions object
      const newPermissions = { canUpdate: false, canDelete: false };
      // Map API response to permission fields
      data.user_actions_list.forEach(item => {
        switch(item.user_action) {
          case 'Create Or Edit Project':
            newPermissions.canUpdate = item.has_access;
            newPermissions.canDelete = item.has_access;
            break;
        }
      });
      setPermissions(newPermissions);
      return data.access_status && data.status === 'Success';
    } catch (error) {
      console.error(`Error checking permission for ${categoryHeader}:`, error);
      return false;
    }
  };

  useEffect(() => {
    if (corporateAccount) {
      fetchDomains();
      checkPermission('Admin Functions');
    }
  }, [corporateAccount]);

  const showErrorDialog = (message, path) => {
    setDialogMessage(message);
    setRedirectPath(path);
    setShowDialog(true);
  };

const handleDialogClose = () => {
  setShowDialog(false);
  if (redirectPath) {
    window.location.href = redirectPath;
  }
};

  const handleDialogCancel = () => {
    setShowDialog(false);
  };

  const handleCreateDomain = () => {
    setSelectedRows(new Set());
    console.log('fetching domains...');
    fetchDomains();
  };

  const handlePageChange = (newPage) => {
    setSelectedRows(new Set());
    setCurrentPage(newPage);
  };

  const handleSearch = () => {
    console.log('searching domains...');
    const filtered = domains.filter(domain =>
      domain.functional_domain.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredDomains(filtered);
    setCurrentPage(1);
  };

  const handleRowEdit = (domain) => {
    if (permissions.canUpdate) {
      setSelectedDomainForEdit(domain);
      setIsEditMode(true);
      setIsCreateDialogOpen(true);
    }
  };




const handleDelete = async () => {
  if (!permissions.canDelete) {
    setError('Insufficient permissions to delete items');
    return;
  }

  if (selectedRows.size === 0) {
    setError('Please select at least one domain to delete');
    return;
  }

  // For single selection, show detailed confirmation
  if (selectedRows.size === 1) {
    const selectedDomainName = Array.from(selectedRows)[0];
    const domain = domains.find(d => d.functional_domain === selectedDomainName);

    setItemToDelete({
      id: selectedDomainName,
      functional_domain: domain?.functional_domain
    });

    setIsDeleteConfirmOpen(true);
    return;
  }

  // For multiple selections, create a summary object
  const multipleItemsObject = {
    id: 'multiple',
    description: `${selectedRows.size} selected domain${selectedRows.size > 1 ? 's' : ''}`,
    actualIds: Array.from(selectedRows)
  };

  setItemToDelete(multipleItemsObject);
  setIsDeleteConfirmOpen(true);
};

// 5. Add the handleDeleteConfirmed function:
const handleDeleteConfirmed = async () => {
  setIsDeleteConfirmOpen(false);
  setIsDeleting(true);
  setDeleteError(null);
  setError(null);

  try {
    // Handle both single and multiple deletions
    const domainNames = itemToDelete.id === 'multiple'
      ? itemToDelete.actualIds
      : [itemToDelete.id];

    // Delete domains one by one since the API only accepts one domain at a time
    console.log('delete url', appConfig.getUrl('/delete_functional_domain'));

    const deletePromises = domainNames.map(domainName =>
      fetch(appConfig.getUrl('/delete_functional_domain'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          functional_domain: domainName
        }),
      })
    );

    const responses = await Promise.all(deletePromises);
    const results = await Promise.all(responses.map(response => response.json()));

    // Check if all deletions were successful
    const failedDeletions = results.filter(result => result.status !== 'Success');

    if (failedDeletions.length === 0) {
      const message = domainNames.length === 1
        ? 'Functional domain deleted successfully'
        : 'Functional domains deleted successfully';
      setError(message);

      // Clear selections and refresh after showing success message
      setTimeout(() => {
        setSelectedRows(new Set());
        console.log('fetching domains after delete...');
        fetchDomains();

        // Clear the success message after refresh
        setTimeout(() => {
          setError(null);
        }, 2000);
      }, 2000);
    } else {
      throw new Error(`Failed to delete ${failedDeletions.length} domain(s)`);
    }
  } catch (err) {
    setDeleteError(err.message);
    setError('Failed to delete domains: ' + err.message);
  } finally {
    setIsDeleting(false);
    setItemToDelete(null);
  }
};



  useEffect(() => {
    if (searchQuery === '' && corporateAccount) {
      console.log('Search cleared, fetching all domains');
      fetchDomains();
    }
  }, [searchQuery]);

  useEffect(() => {
    setFilteredDomains(domains);
  }, [domains]);

  useEffect(() => {
    const setInitialLoad = async () => {
      if (!login_token) {
        showErrorDialog('Invalid login credentials. Please login first', '/LoginAndAuthentication');
        return;
      }
      try {
        const result = await getUserInfo({
          login_token: login_token
        });

        if (result.success) {
          setCorporateAccount(result.userInfo.corporate_account);
          setUserId(result.user_id);
          setUserName(result.userInfo.user_name);
          setAccountDescription(result.userInfo.account_description);
          console.log('User Info:', result.userInfo);
        } else {
          showErrorDialog('Invalid login credentials. Please login again', '/LoginAndAuthentication');
        }
      } catch (error) {
        console.error('Error calling API:', error);
      }
    };

    setInitialLoad();
  }, []);

  const fetchDomains = async () => {
    setLoading(true);
    setSelectedRows(new Set());
    setCurrentPage(1);

    try {
      const requestBody = {
        corporate_account: corporateAccount
      };

        console.log('fetching domains url', appConfig.getUrl('/get_functional_domains'));
      const response = await fetch(appConfig.getUrl('/get_functional_domains'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch domains: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.status === 'Success') {
        setDomains(data.functional_domain_list || []);
      } else {
        setDomains([]);
        if (data.functional_domain_list?.length === 0) {
          setDialogMessage('No Functional Domains found to load.\nCreate new domains to get started');
          setShowDialog(true);
        } else {
          throw new Error(data.status_description || 'Failed to fetch domains');
        }
      }
    } catch (err) {
      console.error('Error fetching domains:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedRows.size === paginatedDomains.length) {
      setSelectedRows(new Set());
    } else {
      const newSelected = new Set(paginatedDomains.map(domain => domain.functional_domain));
      setSelectedRows(newSelected);
    }
  };

  const toggleRowSelection = (domainName) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(domainName)) {
      newSelected.delete(domainName);
    } else {
      newSelected.add(domainName);
    }
    setSelectedRows(newSelected);
  };

  // Pagination calculations
  const totalPages = Math.ceil((sortedDomains?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDomains = (sortedDomains || []).slice(startIndex, startIndex + itemsPerPage);

  if (loading) return <div>Loading...</div>;

  const mainStyles = {
    headerHeight: '64px',
    sectionHeaderHeight: '48px'
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="flex h-[calc(100vh-64px)]">
        <div className="flex-1 overflow-y-auto p-6">
          <div style={styles.container}>

            {/* Title bar */}
            <h1 className="text-l font-bold text-blue-800 mt-6 mr-20 text-center">Manage Functional Domains</h1>
            <div className="flex items-center mb-4 mt-3 justify-between bg-blue-100 p-3 rounded">

              {/* Search bar */}
              <div className="flex items-center">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  placeholder="Search domains..."
                  className="p-2 pr-10 border rounded w-full"
                />
                <button
                  onClick={handleSearch}
                  className="ml-2 p-2 rounded bg-blue-500 hover:bg-blue-600 text-white"
                >
                  <Search size={18} />
                </button>
              </div>

              <h2 className="text-l font-bold text-blue-700 text-center flex-1">Account : {corporateAccount} - {accountDescription} </h2>

              <button
                disabled={!permissions.canUpdate}
                onClick={() => {
                  if (permissions.canUpdate) {
                    setIsEditMode(false);
                    setSelectedDomainForEdit(null);
                    setIsCreateDialogOpen(true);
                  }
                }}
                className={`group relative flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${!permissions.canUpdate ? 'opacity-50' : ''} `}
              >
                <Plus size={18} className="mr-2" />
                Create New

                {/* Hover tooltip for disabled state */}
                {!permissions.canUpdate && (
                  <div className="absolute right-0 top-full mt-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                    Insufficient permission
                    <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-600"></div>
                  </div>
                )}
              </button>
            </div>
            {/* End Title bar */}

            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>
                    <input
                      type="checkbox"
                      checked={paginatedDomains.length > 0 && selectedRows.size === paginatedDomains.length}
                      onChange={toggleSelectAll}
                      style={styles.checkbox}
                    />
                    Select All
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort('functional_domain')}
                    className="select-none hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <span style={{ textDecoration: 'underline' }}>Domain Name</span>
                      <span className="ml-1">
                        {sortConfig.key === 'functional_domain' ? (
                          sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                        ) : null}
                      </span>
                    </div>
                  </th>
                  <th style={{
                    backgroundColor: '#E5E4E2',
                    color: '#0064e5',
                    padding: '6px 12px',
                    textAlign: 'left',
                    borderBottom: '2px solid #ddd',
                    fontSize: '0.875rem',
                    lineHeight: '1',
                    height: '32px'
                  }}>
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {paginatedDomains.map(domain => (
                  <tr key={domain.functional_domain}
                      style={{
                        backgroundColor: selectedRows.has(domain.functional_domain) ? '#DEEFF5' : 'transparent'
                      }}
                      className="border-t hover:bg-gray-50"
                  >
                    <td style={styles.td}>
                      <input
                        type="checkbox"
                        checked={selectedRows.has(domain.functional_domain)}
                        onChange={() => toggleRowSelection(domain.functional_domain)}
                        style={styles.checkbox}
                      />
                    </td>
                    <td style={styles.td}>{domain.functional_domain}</td>
                    <td style={styles.td}>
                      <div className="group relative">
                        <button
                          onClick={() => handleRowEdit(domain)}
                          disabled={!permissions.canUpdate}
                          className={`p-1 rounded hover:bg-blue-100 ${
                            !permissions.canUpdate ? 'opacity-50 ' : 'cursor-pointer'
                          }`}
                          style={{ color: permissions.canUpdate ? '#4CAF50' : '#999' }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.828-2.828z" />
                          </svg>
                        </button>

                        {/* Edit button tooltip */}
                        {!permissions.canUpdate && (
                          <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                            Insufficient permission
                            <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {sortedDomains.length === 0 && (
                  <tr>
                    <td colSpan="3" className="px-4 py-8 text-center text-gray-500">
                      No domains found. Create a new domain or adjust your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Error/Success Messages */}
            {error && (
              <div className={`mb-4 p-2 rounded ${
                error.toLowerCase().includes('successfully')
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-500'
              }`}>
                {error}
              </div>
            )}

            {/* Pagination controls */}
            {!loading && sortedDomains.length > 0 && (
              <div className="mt-4 flex justify-between items-center bg-white p-4 rounded-lg shadow">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>Total Entries: {sortedDomains.length}</span>
                  <span>| Showing {sortedDomains.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, sortedDomains.length)} of {sortedDomains.length}</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="text-sm text-blue-600">Page {currentPage} of {Math.max(1, totalPages)}</span>
                  <button
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages || totalPages === 0}
                    aria-label="Next page"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                <div className="flex items-center">
                  <span className="text-sm text-gray-600 mr-2">Items per page:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1); // Reset to first page when changing items per page
                    }}
                    className="border rounded p-1 text-sm"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
            )}
            {/* End Pagination controls */}

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '20px',
              width: '100%'
            }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div className="group relative">
                  <button
                    style={{
                      ...styles.deleteButton,
                      padding: '8px 16px',
                      ...(isDeleting || selectedRows.size === 0 || !permissions.canDelete ? styles.disabled : {})
                    }}
                    onClick={handleDelete}
                    disabled={isDeleting || selectedRows.size === 0 || !permissions.canDelete}
                  >
                    {isDeleting ? 'Deleting...' : `Delete Selected (${selectedRows.size})`}
                  </button>

                  {/* Delete button tooltip */}
                  {(selectedRows.size === 0 || !permissions.canDelete) && (
                    <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                      {selectedRows.size === 0
                        ? "Select one or more items to delete"
                        : "Insufficient permission"
                      }
                      <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                    </div>
                  )}
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
              </div>
            </div>
          </div>
        </div>
      </div>

{showDialog && (
  <Dialog
    message={dialogMessage}
    onClose={handleDialogClose}
    onCancel={handleDialogCancel}
  />
)}

{/* Delete Confirmation Dialog */}
{renderDeleteConfirmDialog()}

      <CreateFunctionalDomainDialog
        isOpen={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(false);
          setIsEditMode(false);
          setSelectedDomainForEdit(null);
        }}
        onSubmit={handleCreateDomain}
        corporateAccount={corporateAccount}
        isEditMode={isEditMode}
        domainToEdit={selectedDomainForEdit}
      />
    </div>
  );
};

export default FunctionalDomains;